import React from 'react'
import cls from 'classnames'
import { Link as LinkComponent } from 'react-router-dom'
import styled, { css } from 'styled-components'

/**
 * @typedef {'small'|'medium'|'large'} LinkSize
 */

/**
 * Link size constants.
 * @readonly
 * @enum {string}
 */
const SIZE = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
}

const getFontSizeBySize = (size) => {
  switch (size) {
    case SIZE.SMALL:
      return 'var(--link-text-size-small, var(--caption-text))'
    case SIZE.LARGE:
      return 'var(--link-text-size-large, 18px)'
    default:
      return 'var(--link-text-size-medium, var(--body-text))'
  }
}

/**
 * Props for the Link component.
 * @typedef {Object} LinkProps
 * @property {import('react').ReactNode} children Content of the link
 * @property {string} [href] Destination URL
 * @property {'_self'|'_blank'|'_parent'|'_top'|string} [target] Link target
 * @property {(e: import('react').MouseEvent<HTMLAnchorElement>) => void} [onClick] Click handler
 * @property {LinkSize} [size='medium'] Visual size variant
 * @property {boolean} [disabled=false] When true, link is non-interactive
 * @property {import('react').ElementType} [leftIcon] Optional left icon component
 * @property {import('react').ElementType} [rightIcon] Optional right icon component
 * @property {Object<string, any>} [iconProps] Props passed to icon components
 * @property {string} [className] Additional class names
 */

/**
 * Themed link with optional left/right icons. Forwards extra props to the underlying anchor element.
 * @param {LinkProps} props
 * @returns {JSX.Element}
 */
const Link = ({
  children,
  href,
  target,
  onClick,
  size = SIZE.MEDIUM,
  disabled = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  iconProps = {},
  className,
  ...props
}) => {
  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault()
      return
    }
    if (onClick) {
      onClick(e)
    }
  }

  const isExternalUrl = href && /^https?:\/\//.test(href)

  const linkProps = {
    href: disabled ? undefined : href,
    ...(isExternalUrl ? {} : { to: disabled ? undefined : href }),
    target: disabled ? undefined : target,
    onClick: handleClick,
    className: cls(className, { disabled }),
    size,
    disabled,
    ...(isExternalUrl && target === '_blank' ? { rel: 'noopener noreferrer' } : {}),
    ...props,
  }

  return (
    <StyledLink as={isExternalUrl ? 'a' : undefined} {...linkProps}>
      <LinkContent>
        {LeftIcon && (
          <IconWrapper className="left-icon">
            <LeftIcon {...iconProps} />
          </IconWrapper>
        )}
        <TextContent>{children}</TextContent>
        {RightIcon && (
          <IconWrapper className="right-icon">
            <RightIcon {...iconProps} />
          </IconWrapper>
        )}
      </LinkContent>
    </StyledLink>
  )
}

const StyledLink = styled(LinkComponent)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: var(--link-text-default);
  font-size: ${({ size }) => getFontSizeBySize(size)};
  font-weight: var(--link-font-weight, 500);
  cursor: pointer;
  transition:
    color 0.2s ease,
    opacity 0.2s ease;
  user-select: none;

  ${({ as }) =>
    as === 'button' &&
    css`
      padding: 0;
      border: none;
      background: none;
    `}

  &:focus {
    outline: none;
  }

  &:not(.disabled):not(:disabled) {
    &:hover,
    &.__hover {
      color: var(--link-text-hover);
      text-decoration: underline;
    }
  }

  &.disabled,
  &:disabled {
    opacity: var(--link-disabled-opacity);
    cursor: default;
    pointer-events: none;
  }
`

const LinkContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;

  svg {
    width: ${({ size }) => {
      switch (size) {
        case SIZE.SMALL:
          return '16px'
        case SIZE.LARGE:
          return '24px'
        default:
          return '20px'
      }
    }};
    height: ${({ size }) => {
      switch (size) {
        case SIZE.SMALL:
          return '16px'
        case SIZE.LARGE:
          return '24px'
        default:
          return '20px'
      }
    }};
  }

  svg,
  svg > path {
    fill: currentColor;
  }
`

const TextContent = styled.span`
  display: inline-block;
`
/**
 * Expose size constants for external usage.
 * @type {{SMALL: LinkSize, MEDIUM: LinkSize, LARGE: LinkSize}}
 */
Link.SIZE = SIZE

export default Link
